import { checkpointState } from '@ofmos/shared/checklist';
import { checkpointDateLabel, toCheckpointField } from '../../app/record/checklist';
import { todayKey } from '../../app/record/dueDate';
import { cx } from '../../ui';
import { useFieldContext } from '../FieldContext';
import { formatDate } from '../format';
import { EmptyValue } from './EmptyValue';

/**
 * A DATE, AND IN A DENSE ROW A CHECKPOINT IS A DOT (`docs/decisions/phase-b-coach.md` D5).
 *
 * The seeded "Onboarding" view puts EIGHT date columns side by side, and eight full dates is
 * 88 characters of digits across a row nobody can scan. A checkpoint column answers one
 * question at a glance - did this happen, and is it late - so the cell is a 10px dot plus the
 * short date when it is set, and a hairline ring when it is not.
 *
 * `dense` AND `checkpoint`, both required. Dense alone is every table cell in the product, and
 * a checkpoint on the RECORD PAGE is a checklist row rather than a cell - the section owns its
 * own circle there, at 22px, with a label and a deadline sentence beside it.
 *
 * THE WARNING RING NEEDS THE BASE COLUMN IN THE ROW, and D5 says so explicitly: the deadline
 * is `base + offsetDays`, the base is another field, and a view that did not SELECT it has no
 * value to count from. `checkpointState` answers `due` with a null deadline in that case
 * rather than guessing, so the ring is plain - which is the honest picture of "this view
 * cannot tell you".
 */
export function DateDisplay() {
  const { field, value, dense, row } = useFieldContext();
  const checkpoint = field.options?.checkpoint;
  const empty = typeof value !== 'string' || value.length === 0;

  if (dense === true && checkpoint !== undefined) {
    const state = checkpointState(
      toCheckpointField(field),
      row ?? { [field.id]: value },
      todayKey(),
    );
    if (empty) {
      /**
       * THE RING IS DECORATION AND THE MEANING IS TEXT, which is `EmptyValue`'s own recipe
       * one row over: an `aria-label` on a `<span>` is not supported by its implicit role, so
       * the sentence goes in a visually hidden span where every reader can reach it. It says
       * more than "empty" does, because in a checkpoint column being late is the fact.
       */
      return (
        <span className="flex items-center">
          <span
            aria-hidden="true"
            className={cx(
              'size-2.5 rounded-full border',
              state.status === 'late' ? 'border-warning' : 'border-line-strong',
            )}
          />
          <span className="sr-only">{state.status === 'late' ? 'late' : 'not set'}</span>
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1.5">
        <span className="size-2.5 shrink-0 rounded-full bg-ink-muted" />
        <time dateTime={value} className="truncate tabular-nums">
          {checkpointDateLabel(value) ?? value}
        </time>
      </span>
    );
  }

  if (empty) {
    return <EmptyValue />;
  }
  return (
    <time dateTime={value} className="block whitespace-nowrap tabular-nums">
      {formatDate(value)}
    </time>
  );
}
